import { useEffect, useState } from 'preact/hooks';
import { api } from '../lib/api.ts';
import { formatDateTime } from '../lib/labels.ts';
import { ErrorBox, Pill } from './ui.tsx';

type Minutes = {
  id: string; status: 'pending' | 'ready' | 'failed'; requestedAt: string; generatedAt: string | null;
  provisional: boolean | null; sha256: string | null; auditSeq: number | null; error: string | null;
};


/** Actas de la sesión: pedir una nueva, ver su estado y descargarla. */
export function MinutesPanel({ eventId, canRequest }: { eventId: string; canRequest: boolean }) {
  const [list, setList] = useState<Minutes[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => api<{ minutes: Minutes[] }>('GET', `/api/events/${eventId}/minutes`).then((r) => setList(r.minutes)).catch((e: Error) => setError(e.message));

  useEffect(() => { void load(); }, [eventId]);
  // Mientras haya un acta generándose, se consulta cada 2 s.
  const pending = list?.some((m) => m.status === 'pending');
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(load, 2000);
    return () => clearInterval(timer);
  }, [pending, eventId]);

  const request = async () => {
    setBusy(true);
    setError(null);
    try {
      await api('POST', `/api/events/${eventId}/minutes`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="block no-print">
      <h2>Actas</h2>
      <ErrorBox error={error} />
      {canRequest && <button class="btn" disabled={busy || pending} onClick={request}>{pending ? 'Generando acta…' : 'Generar acta en PDF'}</button>}
      {list?.length === 0 && <p class="hint">Todavía no hay actas de esta sesión.</p>}
      <ul class="list">
        {list?.map((m) => (
          <li key={m.id}>
            <span>
              {formatDateTime(m.generatedAt ?? m.requestedAt)}
              {m.status === 'ready' && m.sha256 && <span class="num"> · SHA-256 {m.sha256.slice(0, 12)}…</span>}
            </span>
            {m.status === 'pending' && <Pill kind="live">Generando</Pill>}
            {m.status === 'failed' && <span title={m.error ?? ''}><Pill kind="bad">Falló</Pill></span>}
            {m.status === 'ready' && (
              <a class="btn" href={`/api/minutes/${m.id}/pdf`} download>{m.provisional ? 'Descargar (provisional)' : 'Descargar'}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
